import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { T } from "@/content/site";

export default function Process() {
  const { t } = useLang();
  return (
    <section id="process" data-testid="process-section" className="relative bg-[#0F0F0F] pt-[140px] md:pt-[180px]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 mb-16 md:mb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline mb-8">
          <span className="inline-block w-8 h-px bg-[color:var(--sacrvm-gold)] align-middle mr-3" />
          {t(T.process.overline)}
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="font-serif text-white text-[48px] md:text-[80px] leading-[1] tracking-[-0.01em] max-w-[1000px]"
        >
          {t(T.process.title)}
        </motion.h2>
      </div>

      {T.process.steps.map((step, i) => (
        <ProcessStep key={step.n} step={step} index={i} flip={i % 2 === 1} />
      ))}
    </section>
  );
}

function ProcessStep({ step, index, flip }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.0]);

  return (
    <div ref={ref} className="relative py-16 md:py-28 border-t border-white/5 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className={"grid grid-cols-12 gap-8 md:gap-16 items-center " + (flip ? "md:[direction:rtl]" : "")}>
          <div className="col-span-12 md:col-span-7 [direction:ltr]">
            <div className="img-hover relative aspect-[4/3] md:aspect-[16/10] overflow-hidden">
              <motion.img
                src={step.image}
                alt={t(step.title)}
                loading="lazy"
                style={{ y, scale }}
                className="absolute inset-[-6%] w-[112%] h-[112%] object-cover"
              />
              <div className="absolute inset-0 bg-black/10" />
              <div className="absolute top-6 left-6 overline !text-white/70">Cap. {step.n}</div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 [direction:ltr]">
            <div className="font-serif text-[color:var(--sacrvm-gold)] text-[80px] md:text-[120px] leading-none tracking-[-0.02em]">
              {step.n}
            </div>
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="font-serif text-white text-[32px] md:text-[48px] mt-3 leading-[1.1]"
            >
              {t(step.title)}
            </motion.h3>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 1 }}
              className="mt-6 text-[color:var(--sacrvm-text-muted)] text-[16px] md:text-[17px] leading-[1.75] max-w-[440px]"
            >
              {t(step.body)}
            </motion.p>
          </div>
        </div>
      </div>
    </div>
  );
}
